import { requiredString } from "common";
import { isNil } from "lodash";
import {
    CheckInPreference,
    CheckInValidationCount,
    CheckInValidationType,
    Experience,
    QrCodePreference,
    TicketGenerationStrategy,
    TicketSyntax,
} from "types";
import { array, boolean, lazy, mixed, number, object } from "yup";
import { filterCheckInWindowValidations } from "../ExperienceCheckIn/CheckInWindow/PurchaseCutoffScheduleInput";
import { CheckInPreferenceFormValues } from "../ExperienceCheckIn/ExperienceCheckIn.types";

export const getInitialQrCodeValues = (experience: Experience): QrCodePreference => {
    const { qrCodePreference } = experience;

    return {
        enabled: qrCodePreference?.enabled ?? false,
        ticketGenerationStrategy: qrCodePreference?.ticketGenerationStrategy ?? TicketGenerationStrategy.BOOKING,
        ticketSyntax: qrCodePreference?.ticketSyntax ?? TicketSyntax.TICKET_CODE,
    };
};

export const getValidationSchema = () => {
    return object().shape({
        checkInPreference: object().nullable().shape(validationSchema),
        purchaseExpirationOffset: lazy((value) => {
            if (!isNil(value)) {
                return number().min(0, "Must be 0 or positive integer");
            }

            return mixed();
        }),
        firstCheckinExpirationOffset: lazy((value) => {
            if (!isNil(value)) {
                return number().min(0, "Must be 0 or positive integer");
            }

            return mixed();
        }),
        qrCodePreference: getQrCodePreferenceValidationSchema(),
    });
};

export const getQrCodePreferenceValidationSchema = () => {
    return object().shape({
        enabled: boolean().required(),
        ticketGenerationStrategy: requiredString(),
    });
};

export const validationSchema = {
    enabled: boolean(),
    multiCheckInEnabled: boolean(),
    checkInWindowEnabled: boolean(),
    validations: array().of(
        object().shape({
            object: requiredString(),
            max: number().test("valid", "limit can't be less than 2", (value, context) => {
                const isMultiCheckInEnabled = !!(context as any).from[1].value.multiCheckInEnabled;

                if (context.parent.object === CheckInValidationType.COUNT && isMultiCheckInEnabled) {
                    return !!value && value >= 2;
                }

                return true;
            }),
        }),
    ),
};

export const getCheckInPreferenceValues = (preference?: CheckInPreference): CheckInPreferenceFormValues | null => {
    if (!preference) return null;

    const countValidation = preference.validations.find(
        (validation) => validation.object === CheckInValidationType.COUNT,
    ) as CheckInValidationCount | undefined;

    return {
        ...preference,
        multiCheckInEnabled: countValidation ? countValidation.max > 1 : preference.enabled,
        checkInWindowEnabled: filterCheckInWindowValidations(preference.validations).length > 0,
    };
};
